import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  getMyPublicBookingPhoneVerificationState,
  PublicBookingPhoneVerificationError,
  type PublicBookingPhoneVerificationState,
} from '@/services/publicBookingPhoneVerification';

export const publicBookingPhoneVerificationStateKeys = {
  all: ['public-booking-phone-verification-state'] as const,
  detail: (userId?: string | null) => [...publicBookingPhoneVerificationStateKeys.all, userId ?? 'anonymous'] as const,
};

export interface UsePublicBookingPhoneVerificationStateOptions {
  enabled?: boolean;
  userId?: string | null;
}

export interface UsePublicBookingPhoneVerificationStateResult {
  data: PublicBookingPhoneVerificationState | null;
  phoneVerificationState: PublicBookingPhoneVerificationState | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: PublicBookingPhoneVerificationError | Error | null;
  refetch: UseQueryResult<PublicBookingPhoneVerificationState, PublicBookingPhoneVerificationError | Error>['refetch'];
  invalidate: () => Promise<void>;
}

export function usePublicBookingPhoneVerificationState(
  options: UsePublicBookingPhoneVerificationStateOptions = {},
): UsePublicBookingPhoneVerificationStateResult {
  const queryClient = useQueryClient();
  const enabled = options.enabled ?? true;

  const query = useQuery<PublicBookingPhoneVerificationState, PublicBookingPhoneVerificationError | Error>({
    queryKey: publicBookingPhoneVerificationStateKeys.detail(options.userId),
    queryFn: getMyPublicBookingPhoneVerificationState,
    enabled,
    staleTime: 0,
    gcTime: 5 * 60_000,
    retry: (failureCount, error) => {
      if (error instanceof PublicBookingPhoneVerificationError) {
        return false;
      }

      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    data: query.data ?? null,
    phoneVerificationState: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error ?? null,
    refetch: query.refetch,
    invalidate: async () => {
      await queryClient.invalidateQueries({ queryKey: publicBookingPhoneVerificationStateKeys.all });
    },
  };
}